import PropTypes from "prop-types";
import { useState, useEffect, useCallback } from "react";
import {
  Typography,
  Button,
  Space,
  InputNumber,
  Select,
  Form,
  Flex,
  Tabs,
} from "antd";
import {
  Query,
  Builder,
  Utils as QbUtils,
} from "@react-awesome-query-builder/antd";
import { AntdConfig } from "@react-awesome-query-builder/antd";

import "@react-awesome-query-builder/antd/css/styles.css";
import { useSessionStore } from "../../../store/session-store.js";
import { useAxiosPrivate } from "../../../hooks/useAxiosPrivate";
import { useAlertStore } from "../../../store/alert-store";
import { useExceptionHandler } from "../../../hooks/useExceptionHandler";
import "./DBRules.css";
const { Title } = Typography;
const { TabPane } = Tabs;

function DBRules({ connDetails, workflowDetails }) {
  const { sessionDetails } = useSessionStore();
  const { setAlertDetails } = useAlertStore();
  const [promptKeys, setPromptKeys] = useState({});
  const [promptKeysJson, setPromptKeysJson] = useState({});
  const [promptKeysByValue, setPromptKeysByValue] = useState({});
  const [loading, setLoading] = useState(false);
  const [currentRule, setCurrentRule] = useState({});
  const [currentPercentage, setCurrentPercentage] = useState(0);
  const [ruleLogic, setRuleLogic] = useState("OR");
  const [syncWith, setSyncWith] = useState("Queue");
  const [currentSettings, setCurrentSettings] = useState();
  const handleException = useExceptionHandler();
  const axiosPrivate = useAxiosPrivate();
  const { Option } = Select;
  const operators = [
    "less",
    "greater",
    "equal",
    "not_equal",
    "less_or_equal",
    "greater_or_equal",
    "starts_with",
    "ends_with",
    "contains",
    "not_contains",
  ];
  const onChangePercentage = (value) => {
    setCurrentPercentage(value);
  };
  const handleRuleLogicChange = (value) => {
    setRuleLogic(value);
  };
  const configValue = {
    ...AntdConfig,
    fields: {
      confidence: {
        label: "Filter by Confidence",
        type: "!struct",
        subfields: promptKeys,
      },
      filterByValue: {
        label: "Filter By Value",
        type: "!struct",
        subfields: promptKeysByValue,
      },
      jsonField: {
        label: "Filter by Value (JSON Sub-Field)",
        type: "!struct",
        subfields: promptKeysJson,
      },
    },
    settings: {
      ...AntdConfig.settings,
      maxChildren1: 2,
      showLabels: true,
      canAddGroup: true,
      canAddRule: true,
      canDeleteGroup: true,
      canDeleteRule: true,
    },
  };

  const [config, setConfig] = useState(configValue);
  const [currentTree, setCurrentTree] = useState(
    QbUtils.validateTree(
      QbUtils.loadTree({
        id: QbUtils.uuid(),
        type: "group",
        children1: {},
      }),
      config
    )
  );
  const basePath = `/api/v1/unstract/${sessionDetails?.orgId.replaceAll(
    '"',
    ""
  )}`;
  const path = `${basePath}/manual_review`;
  useEffect(() => {
    if (Object.keys(promptKeys).length > 0) {
      setConfig((prevConfig) => ({
        ...prevConfig,
        fields: {
          ...prevConfig.fields,
          confidence: {
            ...prevConfig.fields.confidence,
            subfields: promptKeys,
          },
          filterByValue: {
            ...prevConfig.fields.filterByValue,
            subfields: promptKeysByValue,
          },
          jsonField: {
            ...prevConfig.fields.jsonField,
            subfields: promptKeysJson,
          },
        },
      }));
    }
  }, [promptKeys, promptKeysJson, promptKeysByValue]);

  useEffect(() => {
    const fetchRegistryData = async () => {
      const response = await fetch(
        `${basePath}/prompt-studio/registry/?prompt_registry_id=${workflowDetails?.tool_instances[0]?.tool_id}`
      );
      if (!response.ok) {
        throw new Error("Failed to fetch registry data");
      }
      const data = await response.json();
      const keys = data[0]?.tool_metadata?.outputs.map((user) => user.name);
      const promptKeySubFields = {};
      keys.forEach((key) => {
        promptKeySubFields[key] = {
          label2: key,
          type: "number",
          valueSources: ["value"],
          fieldSettings: { min: 0, max: 100 },
          operators: operators,
          mainWidgetProps: {
            valueLabel: "Confidence value (%)",
            valuePlaceholder: "Enter Confidence value",
          },
        };
      });
      setPromptKeys(promptKeySubFields);
      const promptKeyJsonSubFields = {};
      keys.forEach((key) => {
        promptKeyJsonSubFields[key] = {
          label: key,
          type: "!group",
          operators: operators,
          subfields: {
            key: {
              label: "Key",
              type: "text",
              valueSources: ["value"],
              operators: operators,
            },
            value: {
              label: "Value",
              type: "text",
              valueSources: ["value"],
              operators: operators,
            },
          },
        };
      });
      setPromptKeysJson(promptKeyJsonSubFields);

      const promptKeyByValueSubFields = {};
      keys.forEach((key) => {
        promptKeyByValueSubFields[key] = {
          label2: key,
          type: "text",
          valueSources: ["value"],
          operators: operators,
          mainWidgetProps: {
            valueLabel: "Value",
            valuePlaceholder: "Enter value",
          },
        };
      });
      setPromptKeysByValue(promptKeyByValueSubFields);
    };
    fetchRegistryData();
  }, []);
  useEffect(() => {
    const option = {
      url: `${path}/db_rules/workflow/${connDetails.workflow}/`,
      method: "GET",
    };

    axiosPrivate(option)
      .then((result) => {
        const ruleData = result?.data?.data;
        const queryValue = JSON.parse(ruleData?.rule_json);
        setRuleLogic(ruleData?.rule_logic);
        setCurrentRule(ruleData);
        setCurrentPercentage(ruleData?.percentage || 0);
        setTimeout(() => {
          setCurrentTree({
            tree: QbUtils.loadTree(queryValue),
            config,
          });
        }, 300);
      })
      .catch((err) => {
        setCurrentRule({});
        setCurrentPercentage(0);
      });
    const optionSettings = {
      url: `${path}/settings/workflow/${connDetails.workflow}/`,
      method: "GET",
    };

    axiosPrivate(optionSettings)
      .then((result) => {
        setCurrentSettings(result?.data?.data);
        setSyncWith(result?.data?.data?.sync_with);
      })
      .catch((err) => {
        setCurrentSettings();
        setSyncWith("Queue");
      });
  }, [connDetails.workflow]);
  const handleSave = () => {
    const jsonTree = QbUtils.getTree(currentTree.tree);
    setLoading(true);
    const ruleString = QbUtils.queryString(currentTree.tree, config);
    const body = {
      percentage: currentPercentage,
      workflow: connDetails.workflow,
      rule_logic: ruleLogic,
      rule_json: JSON.stringify(jsonTree),
      rule_string: ruleString ? ruleString : "",
    };

    const option = {
      url: `${path}/db_rules/`,
      method: currentRule?.id ? "PATCH" : "POST",
      headers: {
        "X-CSRFToken": sessionDetails?.csrfToken,
        "Content-Type": "application/json",
      },
      data: body,
    };

    if (currentRule?.id) {
      option.url = `${path}/db_rules/${currentRule.id}/`;
    }

    axiosPrivate(option)
      .then((result) => {
        setCurrentRule(result?.data);
        setAlertDetails({
          type: "success",
          content: `Successfully ${
            currentRule?.id ? "updated" : "added"
          } the rule.`,
        });
      })
      .catch((err) => {
        setAlertDetails(handleException(err, "Failed to save rule."));
      });
    setLoading(false);
  };
  const handleSaveHITLSettings = () => {
    const option = {
      url: `${path}/settings/`,
      method: currentSettings?.id ? "PATCH" : "POST",
      headers: {
        "X-CSRFToken": sessionDetails?.csrfToken,
        "Content-Type": "application/json",
      },
      data: { sync_with: syncWith, workflow: connDetails.workflow },
    };
    if (currentSettings?.id) {
      option.url = `${path}/settings/${currentSettings?.id}/`;
    }
    axiosPrivate(option)
      .then((result) => {
        setCurrentSettings(result?.data);
        setSyncWith(result?.data?.sync_with);
        setAlertDetails({
          type: "success",
          content: "Sync destination updated successfully.",
        });
      })
      .catch((err) => {
        setAlertDetails(
          handleException(err, "Failed to update sync destination.")
        );
      });
  };
  const renderBuilder = (props) => (
    <div className="query-builder-container" style={{ width: "100%" }}>
      <div className="query-builder">
        <Builder {...props} />
      </div>
    </div>
  );
  const getTreeJson = (lastPath, treeJson) => {
    const currentId = lastPath.shift();
    if (currentId && treeJson.type === "group") {
      const check = treeJson.children1.find((child) => {
        return child?.id === lastPath[0];
      });
      if (check?.type === "rule_group" && lastPath[0] === check?.id) {
        return check;
      } else {
        return getTreeJson(lastPath, check);
      }
    }
  };
  const validateTree = (treeJson, lastItem, lastPath) => {
    if (!lastItem) {
      return true;
    }
    const copyLastPath = [...lastPath];
    const ruleGroupChild = getTreeJson(copyLastPath, treeJson);
    const ruleGroup = ruleGroupChild?.children1?.find(
      (child) => child.id === lastItem
    );
    if (!ruleGroup) return true; // If no rule group found containing the lastRuleId, return true
    // Count the number of rules in that specific rule group
    const rulesCount = ruleGroupChild.children1?.length || 0;

    // Allow new addition only if fewer than 2 rules in the found rule group
    return rulesCount <= 2;
  };

  const onChange = useCallback((immutableTree, config, action, actions) => {
    setCurrentTree((prevState) => {
      const ruleIdToRemove = action?.id; // Rule ID to be removed (if applicable)
      const lastItem = ruleIdToRemove; // immutableTree.get("children1")?.last() || null;
      const lastPath = action?.path;
      const treeJson = QbUtils.getTree(immutableTree);
      const isTreeValid = validateTree(treeJson, lastItem, lastPath);

      if (isTreeValid) {
        // If the tree is valid, update the tree and store it in backup
        return {
          ...prevState,
          tree: immutableTree, // Update the tree
          config: config, // Update the config
          backupTree: QbUtils.getTree(immutableTree), // Store a backup of the valid tree
        };
      } else {
        // If the tree is invalid, attempt to remove the last rule by ID
        const jsonTree = QbUtils.getTree(immutableTree);

        // Function to remove the rule by ID
        const removeRuleById = (tree, ruleId) => {
          return {
            ...tree, // Spread the original tree to maintain immutability
            children1: tree.children1
              ? tree.children1.filter((child) => {
                  // Check if child is a rule group
                  if (child.type === "rule_group") {
                    return {
                      ...child,
                      children1: child.children1.filter(
                        (rule) => rule.id !== ruleId
                      ),
                    };
                  }
                  return child;
                })
              : [],
          };
        };

        // Remove the rule from the tree
        const updatedTree = removeRuleById(jsonTree, ruleIdToRemove);

        // Convert the updated tree using QbUtils.loadTree and update the state
        return {
          ...prevState,
          tree: QbUtils.loadTree(updatedTree), // Load the updated tree state
        };
      }
    });
  }, []);

  return (
    <Tabs defaultActiveKey="1">
      <TabPane tab="Rules" key="1">
        <div>
          <Flex gap="middle" align="flex-start" vertical>
            <Title level={5}>Percentage of files sent to Manual Review</Title>
            <InputNumber
              addonAfter="%"
              value={currentPercentage}
              onChange={onChangePercentage}
            />
            <Form.Item label="Rule Logic">
              <Select value={ruleLogic} onChange={handleRuleLogicChange}>
                <Option value="OR">OR</Option>
                <Option value="AND">AND</Option>
              </Select>
            </Form.Item>
            <Space className="db-rules-result-section">
              {QbUtils.queryString(currentTree.tree, config)}
            </Space>
            <Query
              {...config}
              value={currentTree.tree}
              renderBuilder={renderBuilder}
              onChange={onChange}
            />
          </Flex>
          <Button type="primary" loading={loading} onClick={handleSave}>
            Save Rules
          </Button>
        </div>
      </TabPane>
      <TabPane tab="Settings" key="2" className="settings-tab">
        <Form.Item label="After approval send result to">
          <Select value={syncWith} onChange={setSyncWith}>
            <Option value="DB">Destination DB</Option>
            <Option value="Queue">Queue</Option>
          </Select>
        </Form.Item>
        <Button
          type="primary"
          loading={loading}
          onClick={handleSaveHITLSettings}
        >
          Save Settings
        </Button>
      </TabPane>
    </Tabs>
  );
}

DBRules.propTypes = {
  connDetails: PropTypes.object.isRequired,
  workflowDetails: PropTypes.object,
};

export { DBRules };
